import Autocomplete, {
  AutocompleteInputChangeReason,
  AutocompleteOwnerState,
  AutocompleteProps
} from '@mui/material/Autocomplete'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import { Theme } from '@mui/material/styles'
import { InputLabel } from '@mui/material'

import { useDebounce } from 'hooks/useDebounce'
import { AutoFillType } from 'utils/autofill'
import { InputEventHandlers } from 'utils/tracking/amplitude/WithFieldInteraction'
import {
  ReactText,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
  memo,
  ChangeEvent,
  ReactNode
} from 'react'
import { Choice } from 'components/Input/ChoiceGroup'
import { SEPARATOR_VALUE } from 'components/PathSelect'
import useEmotionStyles from 'hooks/useEmotionStyles'
import * as CSSTypes from 'csstype'
import useEmotionStyledComponent from 'hooks/useEmotionStyledComponent'
import TooltipButton from '../../Tooltip/Button'
import Tooltip from '../../Tooltip'
import { GenericConditionalWrapper } from 'components/GenericConditionalWrapper'

declare global {
  interface Window {
    MSStream: unknown
  }
}
export interface ValueTransformer<T, R = unknown> {
  selector?: keyof T
  extractor?: (raw: T) => R
}

type SelectProps<T, R> = {
  label: ReactText
  error?: string
  tooltip?: string
  clue?: string
  showTooltip?: boolean
  placeholder?: string
  onTooltip?: (isDisplayed: boolean) => void
  value?: string | T
  name?: string
  valueTransformer?: ValueTransformer<T, R>
  optionLabel: keyof T | ((option: T | string) => string)
  onChange?: (value?: R) => void
  path?: string
  noDebounce?: boolean
  outerLabel?: boolean
  smallTextField?: boolean
  showTooltipOnFieldClick?: boolean
  dependson?: string
} & Omit<
  AutocompleteProps<T, false, false, boolean>,
  'renderInput' | 'onChange' | 'defaultValue' | 'value' | 'onBlur' | 'onFocus'
> &
  Omit<InputEventHandlers<T | null>, 'onChange'>

const SELECT_NO_OPTIONS_TEXT_DEFAULT = 'Keine Ergebnisse'

const STREET_PATHS = [
  'debtors.primary.currentAddress.street',
  'debtors.secondary.currentAddress.street',
  'debtors.primary.previousAddress.street',
  'debtors.secondary.previousAddress.street'
]

const styles = (theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column' as CSSTypes.Property.FlexDirection
  },
  textFieldContainer: {
    display: 'flex',
    justifyContent: 'center'
  },
  labelStyle: {
    fontSize: '14px',
    color: theme.palette.grey[900],
    marginBottom: '8px'
  }
})

type SelectAutoCompleteProps<T> = Omit<
  AutocompleteProps<T, boolean, boolean, boolean>,
  'getOptionDisabled'
> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getOptionDisabled: (option: any) => boolean
  dependson?: string
}

function StyledAutocomplete<T>(props: SelectAutoCompleteProps<T>): JSX.Element {
  const StyledAutocompleteNew = useEmotionStyledComponent<
    (props: AutocompleteProps<T, boolean, boolean, boolean>) => JSX.Element
  >(
    () => ({
      root: {
        zIndex: 1,

        '& .MuiTextField-root': {
          margin: 0
        }
      }
    }),
    Autocomplete
  )

  return <StyledAutocompleteNew {...props} />
}

const Select = <T, R>({
  label,
  error,
  tooltip,
  showTooltip = false,
  clue,
  onTooltip,
  noOptionsText = SELECT_NO_OPTIONS_TEXT_DEFAULT,
  placeholder,
  options = [],
  onChange,
  onBlur,
  onFocus,
  onInputChange,
  value,
  name,
  valueTransformer = {},
  optionLabel,
  open = false,
  path,
  noDebounce,
  outerLabel,
  smallTextField,
  showTooltipOnFieldClick,
  dependson,
  autoHighlight,
  className,
  filterOptions,
  freeSolo,
  loading,
  loadingText,
  autoComplete,
  inputValue,
  renderOption,
  disabled
}: SelectProps<T, R>) => {
  const { textFieldContainer, container, labelStyle } =
    useEmotionStyles<ReturnType<typeof styles>>(styles)
  const { selector, extractor } = valueTransformer
  const [showHint, setHint] = useState<boolean | undefined>(undefined)

  const [isOpen, setOpen] = useState(open)
  const handleOpen = useCallback(
    (wantsToOpen: boolean) => () => {
      setOpen(wantsToOpen)
    },
    [setOpen]
  )

  const [isTooltipDisplayed, setTooltipDisplayed] = useState(showTooltip)
  const onTooltipClick = useCallback(() => {
    const isDisplayed = isOpen ? true : !isTooltipDisplayed

    setTooltipDisplayed(isDisplayed)
    setOpen(false)

    if (onTooltip) {
      onTooltip(isDisplayed)
    }
  }, [isTooltipDisplayed, setTooltipDisplayed, onTooltip, isOpen])

  const inputRef = useRef<HTMLInputElement>(null)

  const inputId =
    label && `text-input-${label.toString().toLowerCase().replace(/\s/g, '')}`

  const isStreetPath = STREET_PATHS.includes(path || '')

  const renderInput = useCallback(
    ({ InputProps, inputProps, ...restTextProps }: TextFieldProps) => {
      const { endAdornment, ...restInputProps } = InputProps!

      return (
        <div className={container}>
          <GenericConditionalWrapper
            condition={Boolean(outerLabel)}
            wrapper={(children: ReactNode) => (
              <InputLabel
                htmlFor={inputId || ''}
                classes={{ root: labelStyle }}
              >
                {label}
                {children}
              </InputLabel>
            )}
          >
            {tooltip && (
              <Tooltip
                open={showHint}
                isAvailable={showTooltipOnFieldClick}
                isMinimalistic={showTooltipOnFieldClick}
              >
                {tooltip}
              </Tooltip>
            )}
            <div className={textFieldContainer}>
              <TextField
                size={smallTextField ? 'small' : undefined}
                label={(!outerLabel && label) || ''}
                id={inputId || ''}
                {...restTextProps}
                inputRef={inputRef}
                variant="outlined"
                color="primary"
                error={!!error}
                helperText={(!isStreetPath && error) || clue}
                FormHelperTextProps={{
                  variant: 'standard',
                  sx: {
                    whiteSpace: 'normal',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word'
                  }
                }}
                placeholder={placeholder}
                inputProps={{
                  ...inputProps,
                  autoCapitalize: 'on',
                  autoComplete: AutoFillType.DISABLED_HARD
                }}
                name={name}
                // eslint-disable-next-line react/jsx-no-duplicate-props
                InputProps={{
                  ...restInputProps,
                  endAdornment
                }}
                sx={Boolean(outerLabel) ? { marginTop: '8px !important' } : {}}
              />
              {tooltip && !showTooltipOnFieldClick && (
                <TooltipButton
                  onClick={onTooltipClick}
                  path={path}
                  isVisible={isTooltipDisplayed}
                />
              )}
            </div>
          </GenericConditionalWrapper>
        </div>
      )
    },
    [
      container,
      outerLabel,
      inputId,
      labelStyle,
      label,
      tooltip,
      showHint,
      textFieldContainer,
      smallTextField,
      error,
      isStreetPath,
      clue,
      placeholder,
      name,
      showTooltipOnFieldClick,
      onTooltipClick,
      path,
      isTooltipDisplayed
    ]
  )

  const getOptionSelected = useMemo(
    () =>
      selector
        ? (option: T, currentOption: T) =>
            `${option[selector]}` === `${currentOption[selector]}`
        : (option: T, currentOption: T) => option === currentOption,
    [selector]
  )

  const getOptionLabel = useMemo(() => {
    if (typeof optionLabel === 'function') {
      return optionLabel
    }

    return (option: T | string) => {
      if (typeof option === 'string') {
        return ''
      }

      return `${option[optionLabel]}`
    }
  }, [optionLabel])

  const renderOptionCallback = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: T) => {
      const currentOptionLabel = getOptionLabel(option)
      if (currentOptionLabel === '') {
        return null
      }
      return (
        <li {...props}>
          <span
            id={
              selector ? `${name || 'option'}_${option[selector]}` : undefined
            }
          >
            {currentOptionLabel}
          </span>
        </li>
      )
    },
    [getOptionLabel, selector, name]
  )

  const valueSearchFunc = useMemo(
    () =>
      typeof value === 'string' && selector
        ? (option: T) => `${option[selector]}` === value
        : (option: T) => option === value,
    [selector, value]
  )

  const [selectedValue, setSelectedValue] = useState<T | null>(null)
  const [inputValueState, setInputValueState] = useState('')

  const setValue = useCallback(
    (selected?: T) => {
      if (!selected) {
        return
      }
      setSelectedValue(selected)
      const currentOptionLabel = getOptionLabel(selected)
      const selectedVal = typeof selected === 'string' ? selected : ''
      setInputValueState(currentOptionLabel || selectedVal)

      // This is used to automatically hide the keyboard after the user selected something
      inputRef.current?.blur()
    },
    [getOptionLabel]
  )

  useEffect(() => {
    setValue(options?.find(valueSearchFunc))
  }, [options, setValue, value, valueSearchFunc])

  const changeHandler = useCallback(
    (_: ChangeEvent<unknown>, choice?: T) => {
      if (choice) {
        setValue(choice)
      }

      if (!onChange) {
        return
      }

      const newValue =
        choice && extractor
          ? extractor(choice)
          : (choice as unknown as R | undefined)

      onChange(newValue)
    },
    [onChange, extractor, setValue]
  )

  const blurHandler = useCallback(() => {
    setTimeout(() => {
      setHint(false)
    }, 300)

    if (onBlur) {
      onBlur(selectedValue)
    }
  }, [onBlur, selectedValue])

  const focusHandler = useCallback(() => {
    setTimeout(() => {
      setHint(Boolean(showTooltipOnFieldClick))
    }, 300)

    if (onFocus) {
      onFocus(selectedValue)
    }
  }, [onFocus, selectedValue, showTooltipOnFieldClick])

  const inputChangeHandler = useCallback(
    (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      event: ChangeEvent<any>,
      newInput: string,
      reason: AutocompleteInputChangeReason
    ) => {
      if (reason === 'input') {
        setInputValueState(newInput)
      }

      if (onInputChange) {
        onInputChange(event, newInput, reason)
      }
    },
    [onInputChange]
  )

  const debouncedInputValueState = useDebounce(inputValueState, 200)

  useEffect(() => {
    if (noDebounce) {
      return
    }

    const lcValue = debouncedInputValueState.toLowerCase()
    const matchingOption = options?.find(
      (option) => getOptionLabel(option).toLowerCase() === lcValue
    )

    if (!matchingOption || matchingOption === selectedValue) {
      return
    }

    setSelectedValue(matchingOption)

    if (!onChange) {
      return
    }

    const newValue = extractor
      ? extractor(matchingOption)
      : (matchingOption as unknown as R | undefined)

    onChange(newValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInputValueState])

  return (
    <>
      <StyledAutocomplete<T>
        open={isOpen}
        onOpen={handleOpen(true)}
        onClose={handleOpen(false)}
        value={selectedValue || ''}
        inputValue={inputValue || inputValueState}
        renderOption={(props, option, state, ownerState) =>
          (renderOption || renderOptionCallback)(
            props,
            option,
            state,
            ownerState as AutocompleteOwnerState<
              T,
              false,
              false,
              boolean,
              'div'
            >
          )
        }
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={getOptionSelected}
        options={options}
        fullWidth
        disableClearable
        renderInput={renderInput}
        noOptionsText={noOptionsText}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={changeHandler as any} // Material UI types are not cool here :/
        onBlur={blurHandler}
        onFocus={focusHandler}
        onInputChange={inputChangeHandler}
        id={name}
        getOptionDisabled={(option: Choice<T>) =>
          (option?.value as unknown as string) === SEPARATOR_VALUE
        }
        dependson={dependson}
        defaultValue=""
        autoHighlight={autoHighlight}
        className={className}
        filterOptions={filterOptions}
        freeSolo={freeSolo}
        loading={loading}
        loadingText={loadingText}
        autoComplete={autoComplete}
        disabled={disabled}
      />
      {tooltip && !showTooltipOnFieldClick && (
        <Tooltip open={isTooltipDisplayed}>{tooltip}</Tooltip>
      )}
    </>
  )
}

export default memo(
  Select,
  (prevProps, nextProps) =>
    JSON.stringify(prevProps) === JSON.stringify(nextProps)
) as typeof Select
